import { useCallback, useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";

export const useChartExport = ({
  graphData,
  dependentAnswerMapping,
  comparisonSeries = [],
  isYearComparisonActive,
  baseYearLabel,
  filters,
  selectedFilters,
  dependentFilters,
  selectedDependentFilters,
  selectedIndependent,
  selectedDependent,
  selectedFile,
  selectedIndepMeta,
  selectedDepMeta,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  const exportToPDF = useCallback(async () => {
    if (isExporting) return;
    setExportError(null);

    const normalizeLabel = (value) => String(value ?? "").trim();
    const getRowWeight = (row) => {
      const parsed = Number(row?.weight);
      if (!Number.isFinite(parsed)) return 1;
      return Math.max(0, parsed);
    };
    const formatCountValue = (value) => {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return "0";
      if (Math.abs(numeric - Math.round(numeric)) < 1e-6) {
        return Math.round(numeric).toLocaleString();
      }
      return numeric.toLocaleString(undefined, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
      });
    };
    const buildOutcomeOrderFromMapping = (mapping = {}, presentSet = new Set()) => {
      const orderedEntries = Object.entries(mapping || {}).sort(([leftKey], [rightKey]) => {
        const leftNum = Number(leftKey);
        const rightNum = Number(rightKey);
        const leftIsNum = Number.isFinite(leftNum);
        const rightIsNum = Number.isFinite(rightNum);
        if (leftIsNum && rightIsNum) return leftNum - rightNum;
        if (leftIsNum) return -1;
        if (rightIsNum) return 1;
        return String(leftKey).localeCompare(String(rightKey));
      });

      return orderedEntries
        .map(([rawCode, entry]) => normalizeLabel(entry?.Display ?? entry?.display ?? rawCode))
        .filter((label) => label && presentSet.has(label));
    };
    const buildCatCatSummary = (rows = []) => {
      const cleanRows = rows.filter((row) => {
        const group = normalizeLabel(row?.group);
        const dep = normalizeLabel(row?.dep);
        return group && dep && getRowWeight(row) > 0;
      });

      const indepCats = Array.from(new Set(cleanRows.map((row) => normalizeLabel(row.group)))).sort(
        (left, right) => String(left).localeCompare(String(right))
      );
      const depCatsPresent = Array.from(
        new Set(cleanRows.map((row) => normalizeLabel(row.dep)).filter(Boolean))
      );

      const byGroupDep = new Map();
      const groupTotals = new Map();
      const outcomeTotals = new Map();
      let totalOverall = 0;

      cleanRows.forEach((row) => {
        const group = normalizeLabel(row.group);
        const dep = normalizeLabel(row.dep);
        const weight = getRowWeight(row);
        const key = `${group}|||${dep}`;

        byGroupDep.set(key, (byGroupDep.get(key) || 0) + weight);
        groupTotals.set(group, (groupTotals.get(group) || 0) + weight);
        outcomeTotals.set(dep, (outcomeTotals.get(dep) || 0) + weight);
        totalOverall += weight;
      });

      return {
        indepCats,
        depCatsPresent,
        byGroupDep,
        groupTotals,
        outcomeTotals,
        totalOverall: totalOverall || 1,
      };
    };
    const measureCaptureBounds = (root) => {
      let maxWidth = Math.max(root?.clientWidth || 0, root?.scrollWidth || 0);
      let maxHeight = Math.max(root?.clientHeight || 0, root?.scrollHeight || 0);

      root?.querySelectorAll?.("*").forEach((element) => {
        maxWidth = Math.max(maxWidth, element.clientWidth || 0, element.scrollWidth || 0);
        maxHeight = Math.max(maxHeight, element.clientHeight || 0, element.scrollHeight || 0);
      });

      return {
        width: Math.max(1, Math.ceil(maxWidth)),
        height: Math.max(1, Math.ceil(maxHeight)),
      };
    };

    const input = document.getElementById("graph-container");
    if (!input) {
      setExportError("Chart is not ready to export.");
      return;
    }

    setIsExporting(true);

    try {
      const { width: captureWidth, height: captureHeight } = measureCaptureBounds(input);
      const canvas = await html2canvas(input, {
        scale: 2,
        backgroundColor: "#ffffff",
        width: captureWidth,
        height: captureHeight,
        windowWidth: captureWidth,
        windowHeight: captureHeight,
        scrollX: 0,
        scrollY: 0,
        onclone: (clonedDoc) => {
          const clonedInput = clonedDoc.getElementById("graph-container");
          if (!clonedInput) return;

          clonedInput.style.width = `${captureWidth}px`;
          clonedInput.style.maxWidth = "none";
          clonedInput.style.overflow = "visible";

          clonedInput.querySelectorAll(".overflow-x-auto").forEach((scrollContainer) => {
            const fullWidth = Math.max(
              scrollContainer.scrollWidth || 0,
              scrollContainer.clientWidth || 0
            );
            scrollContainer.scrollLeft = 0;
            scrollContainer.style.overflowX = "visible";
            scrollContainer.style.overflowY = "visible";
            scrollContainer.style.width = `${fullWidth}px`;
            scrollContainer.style.maxWidth = "none";

            const inner = scrollContainer.firstElementChild;
            if (inner) {
              inner.style.width = `${fullWidth}px`;
              inner.style.maxWidth = "none";
            }
          });
        },
      });
      const pdf = new jsPDF({ orientation: "l", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();

      const margin = 15;
      const headerH = 18;

      const rawStudyLabel = selectedFile?.name || "(not selected)";
      const hasYearComparisonStudy =
        isYearComparisonActive &&
        comparisonSeries.some(
          (seriesItem) => Array.isArray(seriesItem?.graphData) && seriesItem.graphData.length > 0
        );
      const studyNameWithoutYear = String(rawStudyLabel)
        .replace(/\s+\d{4}\s*$/, "")
        .trim();
      const comparisonYearSuffix = comparisonSeries
        .map((seriesItem) => String(seriesItem?.yearLabel || "").trim())
        .filter(Boolean)
        .join(" vs ");
      const studyLabel = hasYearComparisonStudy
        ? `${studyNameWithoutYear || rawStudyLabel} ${baseYearLabel || ""}${comparisonYearSuffix ? ` vs ${comparisonYearSuffix}` : ""}`.trim()
        : rawStudyLabel;
      const indepLabel =
        selectedIndepMeta?.question ||
        selectedIndepMeta?.label ||
        selectedIndependent ||
        "No comparison category selected";
      const depLabel =
        selectedDepMeta?.question || selectedDepMeta?.label || selectedDependent || "(none)";

      const drawHeaderBand = () => {
        pdf.setFillColor(38, 50, 56);
        pdf.rect(0, 0, pageW, headerH, "F");
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(255);
        pdf.setFontSize(13);
        pdf.text("Survey Crosstab Report", margin, 11);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        pdf.text(`Generated ${new Date().toLocaleDateString()}`, pageW - margin, 11, {
          align: "right",
        });
        pdf.setTextColor(0);
      };

      drawHeaderBand();

      let curY = headerH + 10;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(12);
      pdf.text("Study:", margin, curY);
      pdf.setFont("helvetica", "normal");
      pdf.text(pdf.splitTextToSize(studyLabel, pageW - margin * 2), margin + 22, curY);
      curY += 7;

      pdf.setFont("helvetica", "bold");
      pdf.text("Outcome:", margin, curY);
      pdf.setFont("helvetica", "normal");
      pdf.text(pdf.splitTextToSize(depLabel, pageW - margin * 2), margin + 28, curY);
      curY += 7;

      pdf.setFont("helvetica", "bold");
      pdf.text("Comparison:", margin, curY);
      pdf.setFont("helvetica", "normal");
      pdf.text(pdf.splitTextToSize(indepLabel, pageW - margin * 2), margin + 35, curY);
      curY += 6;

      const filterLabels = filters
        .filter((filter) => selectedFilters.includes(filter.id))
        .map((f) => f.display);
      const depFilterLabels = (dependentFilters || [])
        .filter((filter) => (selectedDependentFilters || []).includes(filter.id))
        .map((filter) => filter.display);
      const comparisonFilterLine =
        selectedIndependent && filterLabels.length
          ? `Comparison filters: ${filterLabels.join(", ")}`
          : null;
      const outcomeFilterLine = depFilterLabels.length
        ? `Outcome filters: ${depFilterLabels.join(", ")}`
        : null;
      const filterLine = [outcomeFilterLine, comparisonFilterLine].filter(Boolean).join("   |   ");

      if (filterLine) {
        pdf.setFont("helvetica", "italic");
        pdf.setFontSize(10);
        pdf.setTextColor(90);
        pdf.text(pdf.splitTextToSize(filterLine, pageW - margin * 2), margin, curY);
        pdf.setTextColor(0);
        curY += 8;
      } else {
        curY += 2;
      }

      pdf.setDrawColor(210);
      pdf.line(margin, curY, pageW - margin, curY);
      curY += 8;

      const maxGraphH = pageH * 0.7;
      let graphW = pageW - margin * 2;
      let graphH = (canvas.height * graphW) / canvas.width;
      if (graphH > maxGraphH) {
        const scale = maxGraphH / graphH;
        graphW *= scale;
        graphH = maxGraphH;
      }
      const graphX = (pageW - graphW) / 2;
      pdf.addImage(canvas.toDataURL("image/jpeg", 1), "JPEG", graphX, curY, graphW, graphH);
      curY += graphH + 10;

      pdf.addPage();
      drawHeaderBand();
      curY = headerH + 10;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(13);
      pdf.text(selectedIndependent ? "Crosstab Summary" : "Outcome Summary", margin, curY);
      curY += 6;

      const hasCatCat =
        Boolean(selectedIndependent) &&
        graphData.some((row) => row.group != null) &&
        graphData.some((row) => row.dep != null);
      const firstComparableSeries = comparisonSeries.find(
        (seriesItem) =>
          Array.isArray(seriesItem?.graphData) &&
          seriesItem.graphData.length > 0 &&
          seriesItem.graphData.some((row) => row.group != null) &&
          seriesItem.graphData.some((row) => row.dep != null)
      );
      const hasComparableCatCat = isYearComparisonActive && Boolean(firstComparableSeries);

      if (hasCatCat) {
        const baseSummary = buildCatCatSummary(graphData);
        const comparisonSummary = hasComparableCatCat
          ? buildCatCatSummary(firstComparableSeries?.graphData || [])
          : null;
        const depPresentSet = new Set([
          ...baseSummary.depCatsPresent,
          ...(comparisonSummary?.depCatsPresent || []),
        ]);
        const mappingOrder = [
          ...buildOutcomeOrderFromMapping(dependentAnswerMapping, depPresentSet),
          ...buildOutcomeOrderFromMapping(
            firstComparableSeries?.dependentAnswerMapping || {},
            depPresentSet
          ),
        ];
        const dedupedMapped = Array.from(new Set(mappingOrder));
        const depCats = [
          ...dedupedMapped,
          ...Array.from(depPresentSet)
            .filter((dep) => !dedupedMapped.includes(dep))
            .sort((a, b) => a.localeCompare(b)),
        ];
        const indepCats = Array.from(
          new Set([...baseSummary.indepCats, ...(comparisonSummary?.indepCats || [])])
        ).sort((left, right) => String(left).localeCompare(String(right)));

        const body = [];
        const fmtPct = (value) => `${Number(value || 0).toFixed(1)}%`;
        for (const category of indepCats) {
          const baseCategoryTotal = baseSummary.groupTotals.get(category) || 0;
          const comparisonCategoryTotal = comparisonSummary?.groupTotals.get(category) || 0;

          for (const depLabel of depCats) {
            const baseCount = baseSummary.byGroupDep.get(`${category}|||${depLabel}`) || 0;
            const comparisonCount =
              comparisonSummary?.byGroupDep.get(`${category}|||${depLabel}`) || 0;
            const baseRelPct = baseCategoryTotal ? (baseCount / baseCategoryTotal) * 100 : 0;
            const comparisonRelPct = comparisonCategoryTotal
              ? (comparisonCount / comparisonCategoryTotal) * 100
              : 0;
            const baseOverallPct =
              ((baseSummary.outcomeTotals.get(depLabel) || 0) / baseSummary.totalOverall) * 100;
            const comparisonOverallPct = comparisonSummary
              ? ((comparisonSummary.outcomeTotals.get(depLabel) || 0) /
                  comparisonSummary.totalOverall) *
                100
              : 0;

            if (hasComparableCatCat) {
              body.push([
                category,
                depLabel,
                formatCountValue(baseCount),
                formatCountValue(comparisonCount),
                fmtPct(baseRelPct),
                fmtPct(comparisonRelPct),
                fmtPct(baseOverallPct),
                fmtPct(comparisonOverallPct),
              ]);
            } else {
              body.push([
                category,
                depLabel,
                formatCountValue(baseCount),
                fmtPct(baseRelPct),
                fmtPct(baseOverallPct),
              ]);
            }
          }
        }

        autoTable(pdf, {
          startY: curY + 2,
          head: hasComparableCatCat
            ? [
                [
                  { content: "Category", rowSpan: 2 },
                  { content: "Outcome", rowSpan: 2 },
                  { content: "Total Count", colSpan: 2 },
                  { content: "Relative Frequency (within category)", colSpan: 2 },
                  { content: "Overall Frequency", colSpan: 2 },
                ],
                [
                  { content: baseYearLabel || "Original" },
                  { content: firstComparableSeries?.yearLabel || "Compared" },
                  { content: baseYearLabel || "Original" },
                  { content: firstComparableSeries?.yearLabel || "Compared" },
                  { content: baseYearLabel || "Original" },
                  { content: firstComparableSeries?.yearLabel || "Compared" },
                ],
              ]
            : [
                [
                  "Category",
                  "Outcome",
                  "Total Count",
                  "Relative Frequency (within category)",
                  "Overall Frequency",
                ],
              ],
          body,
          theme: "grid",
          styles: {
            font: "helvetica",
            fontSize: 9.5,
            cellPadding: 2.2,
            lineColor: [230, 230, 230],
            lineWidth: 0.2,
            halign: "center",
            valign: "middle",
          },
          headStyles: {
            fillColor: [38, 50, 56],
            textColor: 255,
            halign: "center",
            valign: "middle",
            fontStyle: "bold",
          },
          columnStyles: hasComparableCatCat
            ? {
                0: { cellWidth: 47 },
                1: { cellWidth: 62 },
                2: { cellWidth: 26 },
                3: { cellWidth: 26 },
                4: { cellWidth: 26 },
                5: { cellWidth: 26 },
                6: { cellWidth: 26 },
                7: { cellWidth: 26 },
              }
            : {
                0: { cellWidth: 47 },
                1: { cellWidth: 62 },
                2: { cellWidth: 34 },
                3: { cellWidth: 82 },
                4: { cellWidth: 56 },
              },
          margin: { left: margin, right: margin },
          didDrawPage: (data) => {
            const pageNumber = data.pageNumber;
            const pages = pdf.internal.getNumberOfPages();
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(9);
            pdf.setTextColor(120);
            pdf.text(`Page ${pageNumber} of ${pages}`, pageW - margin, pageH - 6, {
              align: "right",
            });
            pdf.setTextColor(0);
          },
        });
      } else {
        pdf.setFont("helvetica", "italic");
        pdf.setFontSize(10);
        pdf.text(
          selectedIndependent
            ? "Crosstab table is available when both Comparison and Outcome are categorical."
            : "No comparison category selected. Crosstab table is omitted.",
          margin,
          curY + 5
        );
      }

      pdf.save("crosstab-report.pdf");
    } catch (error) {
      setExportError("Could not export PDF. Please try again.");
    } finally {
      setIsExporting(false);
    }
  }, [
    graphData,
    dependentAnswerMapping,
    comparisonSeries,
    isYearComparisonActive,
    baseYearLabel,
    filters,
    selectedFilters,
    dependentFilters,
    selectedDependentFilters,
    selectedFile,
    selectedIndepMeta,
    selectedIndependent,
    selectedDepMeta,
    selectedDependent,
    isExporting,
  ]);

  return { exportToPDF, isExporting, exportError };
};
